
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { JobDetailedData, JobDetailedStop } from '@/models/jobDetailed';
import { AddressBookAirRoadStop, AddressBookResponse, AddressBookEndpointParam } from '@/models/addressBook';
import { CustomSelectSuburb } from '@/models/jbAdvanced';
import CompanyNameComboboxField from '@/components/fields/CompanyNameComboboxField.vue';
import StreetAddressTextField from '@/components/fields/StreetAddressTextField.vue';
import RoomLevelUnitTextField from '@/components/fields/RoomLevelUnitTextField.vue';
import SuburbAutocompleteField from '@/components/fields/SuburbAutocompleteField.vue';
import ContactDetailsNoteNameTextField from '@/components/fields/ContactDetailsNoteNameTextField.vue';
import ContactDetailsNotePhoneTextField from '@/components/fields/ContactDetailsNotePhoneTextField.vue';
import ContactDetailsNoteTextTextareaField from '@/components/fields/ContactDetailsNoteTextTextareaField.vue';
import AttributesFieldset from '@/components/fieldsets/AttributesFieldset.vue';
import DateTimesFieldset from '@/components/fieldsets/DateTimesFieldset.vue';
import { fetchGetAddressBookAddresses } from '@/helpers/fetchers';
import { getSuburbItem } from '@/helpers/booking';
import _isEqual from 'lodash/isEqual';

@Component({
  components: {
    CompanyNameComboboxField,
    StreetAddressTextField,
    RoomLevelUnitTextField,
    SuburbAutocompleteField,
    ContactDetailsNoteNameTextField,
    ContactDetailsNotePhoneTextField,
    ContactDetailsNoteTextTextareaField,
    AttributesFieldset,
    DateTimesFieldset,
  },
})
export default class ChooseAddressStopFieldset extends Vue {
  @Prop(Object) public value: JobDetailedData;
  @Prop({ type: Number, default: 0 }) public stopIndex: number;
  @Prop(Boolean) public loading: boolean;
  @Prop(Boolean) public disabled: boolean;

  public suburbItems: CustomSelectSuburb[] = [];
  public addresses: string[] = [];
  public isAddressesFetching = false;

  get detailedJob() {
    return this.value;
  }

  set detailedJob(value) {
    this.$emit('input', value);
  }

  get stop() {
    return this.detailedJob?.stops[this.stopIndex] || {};
  }

  set stop(stopValue) {
    this.detailedJob.stops[this.stopIndex] = stopValue;
  }

  public setSuburbItems(newStops: JobDetailedStop[]) {
    if (newStops?.length) {
      this.suburbItems = newStops.map((stop: JobDetailedStop) => {
        return getSuburbItem(stop.suburb, String(stop.post_code), stop.state);
      });
    } else {
      this.suburbItems = [];
    }
  }

  @Watch('detailedJob.stops', { immediate: true })
  private watchDetailedJobStops(newStops: JobDetailedStop[], oldStops: JobDetailedStop[]) {
    if (_isEqual(newStops, oldStops)) {
      return;
    }
    this.setSuburbItems(newStops);
  }

  public setDetailedJobStops() {
    if (!this.suburbItems.length) {
      return;
    }
    const stops = [...this.detailedJob.stops];
    this.suburbItems.forEach((suburbItem, index) => {
      if (suburbItem) {
        stops[index] = {
          ...stops[index],
          suburb: suburbItem.name,
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          post_code: suburbItem.postcode,
          state: suburbItem.state,
        };
      } else {
        stops[index] = {
          ...stops[index],
          suburb: '',
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          post_code: '',
          state: '',
        };
      }
    });
    this.detailedJob.stops = stops;
  }

  @Watch('suburbItems', { immediate: true })
  private watchSuburbItems() {
    this.setDetailedJobStops();
  }

  @Watch('detailedJob.customer', { immediate: true })
  private watchDetailedJobCustomer() {
    if (this.detailedJob?.customer) {
      this.getAddressBookWrapper();
    }
  }

  public addressData: AddressBookAirRoadStop[] = [];

  @Watch('addressData', { immediate: true })
  private watchAddressData() {
    this.addresses = this.addressData.map(address => `${address.name}, ${address.suburb.name}`);
  }

  private getAddressBookWrapper(): void {
    const params: AddressBookEndpointParam = {
      username: '',
      customer: this.detailedJob.customer,
      page: 1,
    };
    this.isAddressesFetching = true;
    fetchGetAddressBookAddresses(params)
      .then((res: AddressBookResponse) => {
        this.addressData = res.results;
      })
      .finally(() => {
        this.isAddressesFetching = false;
      });
  }
}
